import { Link } from "react-router-dom";
import Section from "../parts/Section";
import { useContext, useEffect, useState } from "react";
import { SessionContext } from "../SessionContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import Api from "../Api";
import { CheckboxItem } from "../parts/FormBlocks";
import { Notifications } from "../types";

const Dashboard = () => {
  const sessionContext = useContext(SessionContext);
  const permissions = sessionContext.user!.permissions;

  return (
    <Section className="p-4 pb-32">
      <div className="flex flex-col gap-4">
        <NotificationPanel />

        {permissions.registrations && (
          <Link
            className="border rounded hover:bg-slate-50 font-bold text-lg p-4 flex-1 text-center"
            to="/clientes"
          >
            Clientes
          </Link>
        )}

        {permissions.receipts && (
          <Link
            className="border rounded hover:bg-slate-50 font-bold text-lg p-4 flex-1 text-center"
            to="/financeiro"
          >
            Financeiro
          </Link>
        )}

        {(permissions.myQuotes || permissions.quotesAdmin) && (
          <Link
            className="border rounded hover:bg-slate-50 font-bold text-lg p-4 flex-1 text-center"
            to="/meus-orcamentos"
          >
            Meus orçamentos
          </Link>
        )}

        {permissions.quotesAdmin && (
          <Link
            className="border rounded hover:bg-slate-50 font-bold text-lg p-4 flex-1 text-center"
            to="/aprovar-orcamentos"
          >
            Aprovar orçamentos
          </Link>
        )}

        {permissions.authorizations && (
          <Link
            className="border rounded hover:bg-slate-50 font-bold text-lg p-4 flex-1 text-center"
            to="/autorizacoes"
          >
            Autorizações
          </Link>
        )}

        {permissions.contracts && (
          <Link
            className="border rounded hover:bg-slate-50 font-bold text-lg p-4 flex-1 text-center"
            to="/contratos"
          >
            Contratos
          </Link>
        )}

        {permissions.installations && (
          <Link
            className="border rounded hover:bg-slate-50 font-bold text-lg p-4 flex-1 text-center"
            to="/instalacoes"
          >
            Instalações
          </Link>
        )}

        {permissions.orders && (
          <Link
            className="border rounded hover:bg-slate-50 font-bold text-lg p-4 flex-1 text-center"
            to="/os"
          >
            Ordens de serviço
          </Link>
        )}

        {permissions.stats && (
          <Link
            className="border rounded hover:bg-slate-50 font-bold text-lg p-4 flex-1 text-center"
            to="/estatisticas"
          >
            Estatísticas
          </Link>
        )}

        {permissions.equipments && (
          <Link
            className="border rounded hover:bg-slate-50 font-bold text-lg p-4 flex-1 text-center"
            to="/equipamentos"
          >
            Equipamentos
          </Link>
        )}

        {permissions.users && (
          <Link
            className="border rounded hover:bg-slate-50 font-bold text-lg p-4 flex-1 text-center"
            to="/usuarios"
          >
            Usuários
          </Link>
        )}
      </div>
    </Section>
  );
};

export default Dashboard;

const NotificationPanel = () => {
  const [expanded, setExpanded] = useState(false);
  const [working, setWorking] = useState<boolean | null>(null);
  const [issue, setIssue] = useState(0);

  const { user } = useContext(SessionContext);
  const [notifications, setNotifications] = useState(user!.notifications);
  const changeNotifications =
    (key: keyof Notifications) => (checked: boolean) =>
      setNotifications({ ...notifications, [key]: checked });

  const save = async () => {
    setWorking(null);
    await Api.pushSave(notifications);
    window.location.reload();
  };

  const check = async () => {
    const registration = await navigator.serviceWorker.ready;
    if (!registration) {
      setWorking(false);
      setIssue(1);
      return;
    }

    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      setWorking(false);
      setIssue(2);
      return;
    }

    const { sync } = await Api.pushCheck(subscription.endpoint);
    if (!sync) {
      setWorking(false);
      setIssue(3);
      return;
    }

    setIssue(0);
    setWorking(true);
  };

  useEffect(() => {
    check().catch(() => {
      setWorking(false);
      setIssue(4);
    });
  }, []);

  useEffect(() => {
    navigator.serviceWorker.ready.then(async (registration) => {
      const existing = await registration.pushManager.getSubscription();
      if (existing) return;

      const permission = await Notification.requestPermission();
      if (permission !== "granted") return;

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        // @ts-ignore
        applicationServerKey: urlBase64ToUint8Array(
          process.env.REACT_APP_VAPID_PUBLIC_KEY!
        ),
      });

      await Api.pushSubscribe(user!.uuid, subscription);
      window.location.reload();
    });
  }, [user]);

  const subscribe = async () => {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    await Api.pushSubscribe(user!.uuid, subscription!);
    window.location.reload();
  };

  const unsubscribe = async () => {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    await Api.pushUnsubscribe(subscription!.endpoint);
    window.location.reload();
  };

  if (working === null) {
    return (
      <div className="border rounded-lg text-center p-2">
        <FontAwesomeIcon icon="circle-notch" spin />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div
        className={classNames(
          "rounded-t-lg border cursor-pointer flex items-center py-2 px-4 font-semibold",
          {
            "rounded-b-lg": !expanded,
            "border-b-0": expanded,
            "bg-slate-50": !working,
            "bg-green-50 text-green-700": working,
          }
        )}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex-1">
          {working ? "Notificações ativadas" : "Notificações desativadas"}
        </div>

        <div className="text-sm">
          <FontAwesomeIcon icon={expanded ? "chevron-up" : "chevron-down"} />
        </div>
      </div>

      {expanded && (
        <div className="rounded-b-lg border flex py-4 px-4">
          {!working && (issue === 1 || issue === 2) && (
            <div className="flex-1">
              A configuração deste navegador não está permitindo notificações.
            </div>
          )}

          {!working && issue === 4 && (
            <div className="flex flex-col text-sm gap-2">
              <div>
                Não foi possível ativar as notificações para este dispositivo.
              </div>
              <div>
                Caso esteja usando um iPhone ou iPad, use a função
                "Compartilhar" e adicione o ASM à sua tela de início.
              </div>
            </div>
          )}

          {!working && issue === 3 && (
            <div className="flex-1 text-center py-4">
              <button
                className="font-bold border rounded py-2 px-4 hover:bg-slate-50"
                onClick={subscribe}
              >
                Ativar
              </button>
            </div>
          )}

          {working && (
            <div className="flex flex-col text-sm gap-2">
              <CheckboxItem
                label="Novo orçamento"
                checked={notifications.newQuote}
                onChange={changeNotifications("newQuote")}
              />

              <CheckboxItem
                label="Orçamento aprovado"
                checked={notifications.quoteApproved}
                onChange={changeNotifications("quoteApproved")}
              />

              <CheckboxItem
                label="Orçamento autorizado"
                checked={notifications.quoteAuthorized}
                onChange={changeNotifications("quoteAuthorized")}
              />

              <CheckboxItem
                label="Autorização atualizada"
                checked={notifications.authorizationUpdated}
                onChange={changeNotifications("authorizationUpdated")}
              />

              <div className="flex gap-2">
                <button
                  className="text-sm font-bold border rounded py-2 px-4 mt-2 hover:bg-slate-50"
                  onClick={save}
                >
                  Salvar preferências
                </button>

                <button
                  className="text-sm font-bold border rounded py-2 px-4 mt-2 hover:bg-slate-50"
                  onClick={unsubscribe}
                >
                  Desativar
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return new Uint8Array(Array.from(raw, (char) => char.charCodeAt(0)));
};
